import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Image } from 'expo-image';
import { Link, useRouter } from 'expo-router';
import { Animated, Easing, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, fontFamily, maxContentWidth, radii, space, spacing, type } from '~/ui/tokens';
import { useLoadingBar } from '~/stores/useLoadingBar';
import { useCurrency } from '~/stores/useCurrency';
import { SUPPORTED_CURRENCIES, CURRENCY_METAS } from '~/lib/currency';
import { useMember } from '~/stores/useMember';
import { UserAvatar } from './UserAvatar';
import { travellerDisplayName } from '~/lib/travellerProfile';

type TopNavProps = {
  back?: boolean;
  center?: ReactNode;
  compactCenter?: ReactNode;
  /** Homepage-only product navigation, placed directly after the wordmark. */
  afterBrand?: ReactNode;
  /** Pin the bar to the top of the scroll viewport (web) — used on the results
   * page so the search stays reachable without scrolling back up. */
  sticky?: boolean;
};

/** The phone logo lockup, for surfaces that repeat the header (search sheet). */
export function TopNavLogo() {
  const { width } = useWindowDimensions();
  const veryCompact = width < 390;
  return (
    <Link href="/" asChild>
      <Pressable accessibilityRole="link" accessibilityLabel="SuiteScanner home" style={styles.logoLink}>
        <View style={[styles.logoViewport, styles.logoViewportCompact, veryCompact && styles.logoViewportVeryCompact]}>
          <Image
            source={require('../../assets/images/suitescanner_wordmark.png')}
            // Always above the fold. expo-image lazy-loads by default on web,
            // and the cropped lockup (offset above its clipping box) never
            // counted as visible on the tablet homepage, so it never loaded.
            loading="eager"
            contentFit="contain"
            contentPosition="left"
            accessibilityLabel="SuiteScanner"
            style={styles.logoImage}
          />
        </View>
      </Pressable>
    </Link>
  );
}

export function TopNav({ back = false, center, compactCenter, afterBrand, sticky = false }: TopNavProps) {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const hasAfterBrand = afterBrand != null;
  // Decoupled breakpoints so the nav degrades gracefully instead of snapping
  // to the phone layout the moment it leaves full desktop width:
  //  • < 1200 → phone chrome: small logo + a compact "Search" pill, no tabs.
  //  • ≥ 1200 → product tabs + full search trigger (tablet and up).
  // Below 1200 the fixed-width centred search (up to 560px) plus the logo and
  // actions no longer all fit on one row without the search overlapping the
  // logo — the compact layout avoids that by dropping to a narrower pill and
  // collapsing currency/profile into the hamburger menu.
  const compact = width < 1200;
  const veryCompact = back && width < 360;
  // With the customer name visible, the category pills move below the brand
  // before they compete for space with the currency and account controls.
  const stackCategories = compact && hasAfterBrand && width < 720;
  const showCurrency = true;
  // Strict centring only where the side groups leave room for it; below 1200 the
  // search simply fills the space after the tabs (no overlap). The centre width
  // tracks the viewport so it clears the left group and widens on large screens.
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [hamburgerOpen, setHamburgerOpen] = useState(false);
  const currency = useCurrency((state) => state.selectedCurrency);
  const user = useMember((state) => state.user);
  const openPrompt = useMember((state) => state.openPrompt);
  const customerName = user ? travellerDisplayName(user) : '';
  const setCurrency = useCurrency((state) => state.setCurrency);
  const fetchRates = useCurrency((state) => state.fetchRates);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  return (
    <View style={[styles.bar, sticky && styles.barSticky]}>
      <View style={[styles.inner, compact && styles.innerCompact, !compact && hasAfterBrand && styles.innerWithCategories, stackCategories && styles.innerStacked]}>
        {/* Left and right groups both flex:1 so the centre search sits at the
            true middle of the bar regardless of the logo / actions widths. */}
        <View style={[
          styles.leftGroup,
          compact && !hasAfterBrand && styles.leftGroupCompact,
          hasAfterBrand && styles.leftGroupWithCategories,
          compact && hasAfterBrand && !stackCategories && styles.leftGroupWithCategoriesCompact,
        ]}>
          <View style={styles.brandRow}>
            {back ? (
              <Pressable accessibilityRole="button" accessibilityLabel="Go back" onPress={() => router.back()} style={styles.backButton}>
                <Ionicons name="arrow-back" size={20} color={colors.ink} />
              </Pressable>
            ) : null}
            <Link href="/" asChild>
              <Pressable accessibilityRole="link" accessibilityLabel="SuiteScanner home" style={styles.logoLink}>
                <View style={[styles.logoViewport, compact && styles.logoViewportCompact, veryCompact && styles.logoViewportVeryCompact]}>
                  <Image
                    source={require('../../assets/images/suitescanner_wordmark.png')}
                    loading="eager"
                    contentFit="contain"
                    contentPosition="left"
                    accessibilityLabel="SuiteScanner"
                    style={styles.logoImage}
                  />
                </View>
              </Pressable>
            </Link>
          </View>
          {!stackCategories ? afterBrand : null}
        </View>

        {hasAfterBrand && !center ? null : compact ? (
          compactCenter ? (
            // The compact search drops to its own full-width row below.
            <View style={styles.spacer} />
          ) : center ? (
            <View style={styles.center}>{center}</View>
          ) : (
            <View style={styles.spacer} />
          )
        ) : center ? (
          <View style={styles.centerFill}>{center}</View>
        ) : (
          <View style={styles.spacer} />
        )}

        <View style={[styles.actions, compact && !hasAfterBrand && styles.actionsCompact, hasAfterBrand && styles.actionsWithCategories]}>
          {compact ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Menu"
              accessibilityState={{ expanded: hamburgerOpen }}
              onPress={() => setHamburgerOpen((open) => !open)}
              style={styles.hamburgerButton}
            >
              <Ionicons name={hamburgerOpen ? 'close' : 'menu-outline'} size={24} color={colors.ink} />
            </Pressable>
          ) : (
            <>
              {showCurrency ? (
                <View style={styles.currencyAnchor}>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={`Currency, ${currency === 'EUR' ? 'EURO' : currency}`}
                    accessibilityState={{ expanded: currencyOpen }}
                    onPress={() => setCurrencyOpen((open) => !open)}
                    style={styles.currencyControl}
                  >
                    <Ionicons name="globe-outline" size={17} color={colors.inkSoft} />
                    <Text style={styles.currencyLabel}>{currency === 'EUR' ? 'EURO' : currency}</Text>
                    <Ionicons name={currencyOpen ? 'chevron-up' : 'chevron-down'} size={13} color={colors.inkSoft} />
                  </Pressable>
                  {currencyOpen ? (
                    <>
                      <Pressable
                        accessibilityLabel="Close currency menu"
                        style={styles.currencyBackdrop}
                        onPress={() => setCurrencyOpen(false)}
                      />
                      <View style={styles.currencyMenu}>
                        {SUPPORTED_CURRENCIES.map((code) => (
                          <Pressable
                            key={code}
                            accessibilityRole="menuitem"
                            accessibilityState={{ selected: currency === code }}
                            onPress={() => { setCurrency(code); setCurrencyOpen(false); }}
                            style={[styles.currencyOption, currency === code && styles.currencyOptionSelected]}
                          >
                            <View style={styles.currencyOptionCopy}>
                              <Text style={[styles.currencyOptionCode, currency === code && styles.currencyOptionCodeSelected]}>{code === 'EUR' ? 'EURO' : code}</Text>
                              <Text style={styles.currencyOptionName}>{CURRENCY_METAS[code]?.name ?? code}</Text>
                            </View>
                            {currency === code ? <Ionicons name="checkmark" size={16} color={colors.brand} /> : null}
                          </Pressable>
                        ))}
                      </View>
                    </>
                  ) : null}
                </View>
              ) : null}
              {user ? (
                <Link href="/account" asChild>
                  <Pressable accessibilityRole="button" accessibilityLabel={`${customerName}, account`} style={styles.profileButton}>
                    <UserAvatar user={user} size={29} />
                    {/* On phones the first name alone, on one line: a full name wrapping
                        to two lines made the header a third of the screen. */}
                    <Text style={[styles.profileName, width < 720 && styles.profileNameNarrow, width < 560 && { maxWidth: Math.max(52, width - 275) }]} numberOfLines={width < 560 ? 1 : 2}>{width < 560 ? customerName.split(' ')[0] : customerName}</Text>
                  </Pressable>
                </Link>
              ) : (
                <Pressable accessibilityRole="button" accessibilityLabel="Sign in" onPress={openPrompt} style={[styles.profileButton, styles.signInButton]}>
                  <Ionicons name="person-outline" size={18} color={colors.brand} />
                  <Text style={styles.signInLabel}>Sign in</Text>
                </Pressable>
              )}
            </>
          )}
        </View>
        {stackCategories ? <View style={styles.mobileCategoryRow}>{afterBrand}</View> : null}
      </View>
      {compact && compactCenter ? <View style={styles.compactSearchRow}>{compactCenter}</View> : null}
      {/* A sibling of `inner`, not nested inside it — react-native-web (like
          native) always resolves an absolutely-positioned view against its
          direct parent, regardless of that parent's own position style, so
          nesting this under the hamburger button meant `top` was measured
          from the button itself. When categories wrap to their own row
          (stackCategories), that row's height isn't in the button's box at
          all, and the fixed offset that cleared it in the single-row case
          landed the menu on top of that second row instead — rendering
          over its text rather than below it, which is what read as
          translucent. Anchored to `bar` (this component's own outermost,
          already-positioned view) and offset by top:'100%' instead, it sits
          flush under the bar's actual rendered height, whether that's one
          row or two. */}
      {compact && hamburgerOpen ? (
        <>
          <Pressable
            accessibilityLabel="Close menu"
            style={styles.currencyBackdrop}
            onPress={() => setHamburgerOpen(false)}
          />
          <View style={styles.hamburgerMenu}>
            {user ? (
              <Link href="/account" asChild>
                <Pressable accessibilityRole="button" onPress={() => setHamburgerOpen(false)} style={styles.hamburgerMenuRow}>
                  <UserAvatar user={user} size={24} />
                  <Text style={styles.hamburgerMenuRowText} numberOfLines={1}>{customerName}</Text>
                </Pressable>
              </Link>
            ) : (
              <Pressable accessibilityRole="button" onPress={() => { setHamburgerOpen(false); openPrompt(); }} style={styles.hamburgerMenuRow}>
                <Ionicons name="person-outline" size={20} color={colors.brand} />
                <Text style={[styles.hamburgerMenuRowText, styles.signInLabel]}>Sign in</Text>
              </Pressable>
            )}
            <View style={styles.hamburgerMenuDivider} />
            <View style={styles.hamburgerMenuSectionHeader}>
              <Ionicons name="globe-outline" size={17} color={colors.inkSoft} />
              <Text style={styles.hamburgerMenuSectionLabel}>Currency</Text>
            </View>
            {SUPPORTED_CURRENCIES.map((code) => (
              <Pressable
                key={code}
                accessibilityRole="menuitem"
                accessibilityState={{ selected: currency === code }}
                onPress={() => { setCurrency(code); setHamburgerOpen(false); }}
                style={[styles.currencyOption, currency === code && styles.currencyOptionSelected]}
              >
                <View style={styles.currencyOptionCopy}>
                  <Text style={[styles.currencyOptionCode, currency === code && styles.currencyOptionCodeSelected]}>{code === 'EUR' ? 'EURO' : code}</Text>
                  <Text style={styles.currencyOptionName}>{CURRENCY_METAS[code]?.name ?? code}</Text>
                </View>
                {currency === code ? <Ionicons name="checkmark" size={16} color={colors.brand} /> : null}
              </Pressable>
            ))}
          </View>
        </>
      ) : null}
      <NavProgressBar />
    </View>
  );
}

// The blue loading bar that lives on the nav's bottom border. It fills left to
// right while any API call is in flight (and during route transitions, which
// are API-backed here), then completes and fades. Width is animated on the JS
// driver — a single thin bar, so the layout cost is negligible, and it renders
// identically on web and native.
function NavProgressBar() {
  const inflight = useLoadingBar((state) => state.inflight);
  const active = inflight > 0;
  const progress = useRef(new Animated.Value(0)).current;
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (active) {
      opacity.setValue(1);
      progress.setValue(0);
      // Ease out toward 90% and hold; the completion to 100% happens once the
      // work actually finishes, so the bar never lies about being done.
      Animated.timing(progress, {
        toValue: 0.9,
        duration: 1200,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
    } else {
      Animated.sequence([
        Animated.timing(progress, { toValue: 1, duration: 240, easing: Easing.out(Easing.quad), useNativeDriver: false }),
        Animated.timing(opacity, { toValue: 0, duration: 280, delay: 80, useNativeDriver: false }),
      ]).start(({ finished }) => {
        if (finished) progress.setValue(0);
      });
    }
  }, [active, opacity, progress]);

  const width = progress.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] });

  return (
    <Animated.View pointerEvents="none" style={[styles.progressTrack, { opacity }]}>
      <Animated.View style={[styles.progressFill, { width }]} />
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  bar: {
    position: 'relative',
    backgroundColor: colors.card,
    borderBottomWidth: 1,
    borderBottomColor: colors.brandLine,
    zIndex: 1000,
  },
  // Web-only: keep the header (and its search) pinned while results scroll.
  barSticky: {
    ...({ position: 'sticky', top: 0 } as object),
  },
  progressTrack: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: -1,
    height: 2.5,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: colors.brand,
    borderTopRightRadius: radii.full,
    borderBottomRightRadius: radii.full,
  },
  inner: {
    // Capped and centred at the same maxContentWidth + gutter every page
    // section uses, so the logo lines up with the hero, value-strip heading
    // and search heading below it instead of sitting at the raw viewport edge.
    width: '100%',
    maxWidth: maxContentWidth,
    alignSelf: 'center',
    boxSizing: 'border-box',
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 70,
    paddingVertical: spacing(1),
    paddingHorizontal: space.card,
    gap: spacing(2),
  },
  innerCompact: {
    minHeight: 62,
    paddingHorizontal: space.card,
    gap: spacing(1),
  },
  compactSearchRow: {
    width: '100%',
    maxWidth: maxContentWidth,
    alignSelf: 'center',
    paddingHorizontal: space.card,
    paddingBottom: spacing(1.5),
  },
  innerWithCategories: {
    minHeight: 74,
    paddingVertical: 0,
  },
  innerStacked: {
    flexWrap: 'wrap',
    paddingVertical: spacing(1),
    rowGap: spacing(0.5),
  },
  // Left and right groups grow equally from a zero basis, so the fixed-width
  // centre lands on the true viewport centre rather than drifting with whatever
  // sits on the right. The logo stays left-aligned; the actions right-aligned.
  leftGroup: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(1.5),
  },
  leftGroupWithCategories: {
    flexGrow: 1,
    flexBasis: 'auto',
  },
  leftGroupWithCategoriesCompact: {
    gap: space.micro,
  },
  leftGroupCompact: { flexGrow: 0, flexShrink: 0, flexBasis: 'auto' },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(1),
    flexShrink: 0,
    minWidth: 0,
  },
  backButton: { padding: spacing(0.25) },
  logoLink: { minHeight: 42, justifyContent: 'center' },
  // suitescanner_wordmark.png is the tight-cropped lockup (a 3x raster of the
  // widest viewport), so the image just fills each viewport — no crop offsets.
  logoViewport: { width: 218, height: 38, overflow: 'hidden' },
  // Compact = the full logo crop scaled to 0.8, so the whole "suitescanner"
  // wordmark stays visible instead of the last letter being clipped.
  logoViewportCompact: { width: 178, height: 31 },
  logoViewportVeryCompact: { width: 109, height: 19 },
  logoImage: { width: '100%', height: '100%' },
  spacer: { flex: 1 },
  // The search fills the gap between the tabs and the actions; its own max-width
  // (on the trigger) keeps it tidy and, being centred here, gives equal padding
  // to the tab on its left and the currency/profile on its right.
  // Fixed centre column (max 560): does not grow, so it holds the viewport
  // centre; shrinks only when a narrow window would otherwise overflow.
  centerFill: {
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: 780,
    maxWidth: 780,
    minWidth: 0,
    alignItems: 'center',
  },
  centerCompact: { flex: 1, minWidth: 0, alignItems: 'center' },
  center: { flex: 1, minWidth: 0, alignItems: 'center' },
  actions: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing(0.75),
  },
  actionsWithCategories: {
    flexGrow: 0,
    flexBasis: 'auto',
  },
  actionsCompact: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
  },
  mobileCategoryRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
  },
  currencyAnchor: { position: 'relative', zIndex: 100 },
  currencyBackdrop: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 90,
  },
  currencyControl: {
    flexDirection: 'row',
    gap: spacing(0.5),
    height: 38,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing(1.25),
  },
  currencyLabel: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft },
  currencyMenu: {
    position: 'absolute',
    top: 42,
    right: 0,
    width: 176,
    padding: spacing(0.75),
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.line,
    backgroundColor: colors.card,
    zIndex: 100,
    ...({ boxShadow: '0 10px 24px rgba(16, 36, 80, 0.14)' } as object),
  },
  currencyOption: {
    minHeight: 42,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(1),
    paddingHorizontal: spacing(1),
    borderRadius: radii.sm,
  },
  currencyOptionSelected: { backgroundColor: colors.skyTint },
  currencyOptionSymbol: { width: 20, textAlign: 'center', fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft },
  currencyOptionCopy: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: spacing(1) },
  currencyOptionCode: { width: 48, fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.ink },
  currencyOptionCodeSelected: { color: colors.brand },
  currencyOptionName: { flex: 1, fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkSoft },
  profileButton: { minWidth: 40, minHeight: 40, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing(0.75), paddingHorizontal: spacing(0.5) },
  profileName: { maxWidth: 260, fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.ink },
  profileNameNarrow: { maxWidth: 110 },
  signInButton: { borderWidth: 1, borderColor: colors.brandLine, borderRadius: radii.full, paddingHorizontal: spacing(1.25) },
  signInLabel: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.brand },
  hamburgerButton: { minWidth: 40, minHeight: 40, alignItems: 'center', justifyContent: 'center' },
  hamburgerMenu: {
    position: 'absolute',
    // 100% of `bar` (this view's own direct, positioned parent) — flush
    // under the bar's real rendered height, whether categories wrapped to
    // a second row or not, rather than a fixed guess from one row's height.
    top: '100%',
    right: space.card,
    marginTop: spacing(0.5),
    width: 220,
    padding: spacing(0.75),
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.line,
    backgroundColor: colors.card,
    zIndex: 100,
    ...({ boxShadow: '0 10px 24px rgba(16, 36, 80, 0.14)' } as object),
  },
  hamburgerMenuRow: {
    minHeight: 42,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(1),
    paddingHorizontal: spacing(1),
    borderRadius: radii.sm,
  },
  hamburgerMenuRowText: { fontFamily: fontFamily.medium, fontSize: type.bodyRegular.fontSize, color: colors.ink },
  hamburgerMenuDivider: { height: 1, backgroundColor: colors.lineSoft, marginVertical: spacing(0.5) },
  hamburgerMenuSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(0.75),
    paddingHorizontal: spacing(1),
    paddingTop: spacing(0.5),
    paddingBottom: spacing(0.75),
  },
  hamburgerMenuSectionLabel: {
    fontFamily: fontFamily.semibold,
    fontSize: type.metadata.fontSize,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: colors.inkFaint,
  },
});
